// The confirmed order's "Section Totals & Profitability" card: selling, cost and
// mark-up per tile, and overall.
//
// End-to-end retest 2026-10-07 (QUO-00241, 42 lines): the card matched each line's
// category, as typed, against a client price list's section keys "provision" /
// "cabin" / "additional". A line's category is a catalogue key ("provisions",
// "bonded_stores") or blank, so Provision Stores read US$0.00 beside a full OVERALL;
// D104.21 showed the unmatched lines in an "Uncategorised / other" tile, where the
// provisions then sat. A line's tile is now its department by the owner's rule
// (`utils/itemCategoryKey.departmentOf`, the quotation Excel's grouping): every line
// lands in exactly one tile, and the tiles add up to OVERALL.
//
// D104.10 (QUO-00241 / QUO-00235, order US$4,475.12 against the invoice's US$4,470.56): the card
// summed each line's stored `totalPrice` = ceil(qty x the cost already ceiled to a cent) - the
// unit-then-total reading the quote page retired. Cost now comes from the same owner the quote page
// uses (`resolveItemPricing`), fed the EXACT unit cost, rounded to the cent once per line; see
// `orderLineCost`. Selling is untouched.
import { departmentOf, normalizeCategoryKey } from '../../utils/itemCategoryKey';
import { num, round2 } from '../../utils/invoiceLineMath';
import { resolveItemPricing } from '../../utils/pricing';

export interface SectionTotal {
  selling: number;
  cost: number;
  lines: number;
  /** Lines whose cost is not known yet (D54), left out of `cost`, never read as 0. */
  pendingLines: number;
  markupAmount: number;
  pctVsCost: number | null;
  pctVsSelling: number | null;
  /** A8: mark-up under 20% of cost. */
  alertLowMarkup: boolean;
}

export interface TileTotal extends SectionTotal {
  key: string;
  label: string;
}

// The tiles, in order. The client price list's three sections are always shown (A9,
// D104.21); a store department shows when it has lines; what no tile claims is
// "Uncategorised / other" (D104.21; the quotation Excel prints it as Other Stores).
export const TILES: ReadonlyArray<{ key: string; label: string; always?: boolean }> = [
  { key: 'provisions', label: 'Provision Stores', always: true },
  { key: 'cabin_stores', label: 'Cabin Stores', always: true },
  { key: 'additional', label: 'Additional Items', always: true },
  { key: 'deck_stores', label: 'Deck Stores' },
  { key: 'engine_stores', label: 'Engine Stores' },
  { key: 'bonded_stores', label: 'Bonded Stores' },
  { key: 'other_stores', label: 'Uncategorised / other' },
];
const TILE_KEYS: ReadonlySet<string> = new Set(TILES.map((t) => t.key));

/**
 * A line's tile: a category that names a tile is that tile ("additional", a price
 * list's own section, has no store department); anything else is its department.
 * A Set and the owner's Map, never `key in object`: a category spelled
 * "constructor" must not find Object.prototype's and drop out of every tile.
 */
export function tileOf(category: unknown): string {
  const key = normalizeCategoryKey(category);
  return TILE_KEYS.has(key) ? key : departmentOf(category);
}

type Bucket = Pick<SectionTotal, 'selling' | 'cost' | 'lines' | 'pendingLines'>;

// Null, never 0 - D54 (2026-09-22) leaves a price-list line's cost EMPTY until a
// supplier is picked (TECH_DEBT #342). A line with a totalPrice or a unitPrice states
// its cost; a line with neither is COST PENDING and is left out of the sum.
// This is the STORED cost (cent-ceiled unit, then total); the figure the card shows is `orderLineCost`.
const storedCostTotalOf = (it: any): number | null => {
  if (typeof it.totalPrice === 'number') return it.totalPrice;
  if (typeof it.unitPrice === 'number') return Number(it.quantity || 0) * it.unitPrice;
  return null;
};

// The WHOLE line goes to the owner with only `totalPrice` overridden: a hand-picked
// subset left out `quantity`, and the card disagreed with the header's Total Quote by
// cents (F18, QUO-00199). A pending cost is passed through as null; the owner reads a
// missing total as 0 for the SELLING side only. (TECH_DEBT #146, ClickUp 86d3twk6r.)
// The STORED cost goes in, never the exact one: selling is marked up from the stored cost
// (`markup-cost-once`), so no selling figure moves with D104.10.
const resolvedSellingOf = (it: any) => resolveItemPricing({ ...it, totalPrice: storedCostTotalOf(it) });
const sellingTotalOf = (it: any): number => resolvedSellingOf(it).totalSellingPrice;

/**
 * D172.4 (29 Oct call, Deborah): a line's US$ unit selling price and selling total, for the line table.
 * The SAME owner call as the tiles' `sellingTotalOf` (so the lines add up to the tiles and to Total Quote);
 * nothing is computed here. A stated free-of-charge 0 is shown as the owner returns it (it reprices at
 * markup, as the quotation page's own cells and the tiles do).
 *
 * @returns null when the line states no selling price and has no cost to price from: blank, never the
 *   owner's invented 0.
 */
export function orderLineSelling(it: any): { unit: number; total: number } | null {
  const line = it || {};
  if (num(line.unitSellingPrice) == null && num(line.totalSellingPrice) == null && storedCostTotalOf(line) == null) return null;
  const { unitSellingPrice, totalSellingPrice } = resolvedSellingOf(line);
  return { unit: unitSellingPrice, total: totalSellingPrice };
}

/**
 * The exact US$ unit cost the QUOTATION line states for this order line, when it is still the
 * order line's cost. An order line stores only the cost ceiled to a cent (`unitPrice`) and copies
 * no exact (no stored money may change); the unrounded figure it was ceiled from (`unitPriceExact`,
 * D85) stays on the quotation line at `quotationItemIndex`, which `getConfirmedOrderDetail` already
 * loads onto the page. It is this line's only while no supplier change has moved the cost on the
 * order: `costMovedAt` is absent until one does (the model field, D185.1; the PO price ladder asks
 * the same, `loadCatalogueContext`). That the exact still explains the cent cost is
 * `markupCostUsd`'s own check, made inside `resolveItemPricing`.
 */
const quotedExactCost = (it: any, quotationItems: unknown): number | undefined => {
  if (it?.costMovedAt != null) return undefined;
  const at = it?.quotationItemIndex;
  if (typeof at !== 'number' || !Array.isArray(quotationItems)) return undefined;
  return quotationItems[at]?.unitPriceExact ?? undefined;
};

/**
 * A line's cost the D104.10 way: the owner's (`resolveItemPricing`) unit cost and total, with the
 * unit cost the EXACT one where the quotation states it, and the total (exact unit x qty) rounded
 * to the cent ONCE - the figure the quote page shows for the same line (`resolveQuotationTotals`
 * `lines[].cost`). Whole-cent costs, lines the quotation does not speak for, a moved cost, a zero
 * or credit quantity: the stored total, exactly as before. Rounding per line keeps every tile a sum
 * of cents, so the tiles add up to OVERALL as shown (D104.21).
 *
 * @returns null while the line has no cost yet (D54): pending, never 0. `unitCost` is the owner's,
 *   so 0 for a line that states only a total.
 */
export function orderLineCost(it: any, quotationItems?: unknown): { unitCost: number; totalCost: number } | null {
  const stored = storedCostTotalOf(it || {});
  if (stored == null) return null;
  const { unitCost, totalCost } = resolveItemPricing({
    ...it,
    totalPrice: stored,
    unitPriceExact: it?.unitPriceExact ?? quotedExactCost(it, quotationItems),
  });
  return { unitCost, totalCost: round2(totalCost) };
}

function enrich(b: Bucket): SectionTotal {
  const markupAmount = b.selling - b.cost;
  const pctVsCost = b.cost > 0 ? (markupAmount / b.cost) * 100 : null;
  const pctVsSelling = b.selling > 0 ? (markupAmount / b.selling) * 100 : null;
  return { ...b, markupAmount, pctVsCost, pctVsSelling, alertLowMarkup: pctVsCost !== null && pctVsCost < 20 };
}

/**
 * The tiles to show, in order, and OVERALL - the sum of every line, so of the tiles.
 * `quotationItems`: the linked quotation's lines (D104.10), where an order line's exact cost is.
 */
export function orderSectionTotals(items: any[], quotationItems?: unknown): { tiles: TileTotal[]; overall: SectionTotal } {
  const buckets = new Map<string, Bucket>(TILES.map((t) => [t.key, { selling: 0, cost: 0, lines: 0, pendingLines: 0 }]));
  for (const it of Array.isArray(items) ? items : []) {
    const b = buckets.get(tileOf(it?.category)) as Bucket;
    b.selling += sellingTotalOf(it || {});
    const cost = orderLineCost(it || {}, quotationItems);
    if (cost == null) b.pendingLines += 1;
    else b.cost += cost.totalCost;
    b.lines += 1;
  }
  const overall = TILES.reduce<Bucket>(
    (acc, t) => {
      const b = buckets.get(t.key) as Bucket;
      return {
        selling: acc.selling + b.selling,
        cost: acc.cost + b.cost,
        lines: acc.lines + b.lines,
        pendingLines: acc.pendingLines + b.pendingLines,
      };
    },
    { selling: 0, cost: 0, lines: 0, pendingLines: 0 }
  );
  return {
    tiles: TILES.filter((t) => t.always || (buckets.get(t.key) as Bucket).lines > 0).map((t) => ({
      key: t.key,
      label: t.label,
      ...enrich(buckets.get(t.key) as Bucket),
    })),
    overall: enrich(overall),
  };
}
